import { AppState, LogoutOptions, RedirectLoginOptions, useAuth0 } from '@auth0/auth0-react'
import { Browser } from '@capacitor/browser'

import { useCallback, useEffect, useRef } from 'react'

import { Loading } from '../components/Loading'
import {
  AUTH0_LOGIN_SCOPE,
  cleanupAfterAuthDecline,
  getAuthErrorFromUrl,
  getPostDeclineLogoutReturnTo,
  isNativeApp,
  markSignupFlowPending,
} from '../config/auth0'
import { AuthStateContext } from '../providers/AuthStateContext'

const Register = (): JSX.Element => {
  const isMobileDevice = isNativeApp()
  const { loginWithRedirect, logout } = useAuth0()
  const handledRef = useRef(false)

  const startSignupRedirect = useCallback(() => {
    // separate redirectUrl like this since mobile devices don't get appState after redirect
    AuthStateContext.setRedirectUrl('/hospitals')

    let loginParams: RedirectLoginOptions<AppState> = {
      appState: { fromSignup: true, redirectUrl: '/hospitals' },
      authorizationParams: { scope: AUTH0_LOGIN_SCOPE, screen_hint: 'signup' },
    }
    if (isMobileDevice) {
      loginParams = {
        ...loginParams,
        async openUrl(url) {
          // Redirect using Capacitor's Browser plugin
          await Browser.open({
            url,
            windowName: '_self',
          })
        },
      }
    }

    // Set the signup flow flag in localStorage before redirecting.
    // - Web: localStorage persists through Auth0's full-page redirect (same origin, same tab).
    // - Mobile: the main WebView never navigates away (Browser plugin opens a separate sheet),
    //   so localStorage is always intact when the deep link returns.
    // onRedirectCallback (web) and appUrlOpen (mobile) in Auth0ProviderWithHistory /
    // AuthStateContext also set this flag as a backup, but this line is the most reliable
    // trigger since it runs immediately before the redirect starts.
    markSignupFlowPending()
    loginWithRedirect(loginParams)
  }, [isMobileDevice, loginWithRedirect])

  useEffect(() => {
    if (handledRef.current) return

    if (getAuthErrorFromUrl()) {
      handledRef.current = true
      cleanupAfterAuthDecline()
      const logoutOptions: LogoutOptions = {
        logoutParams: {
          returnTo: getPostDeclineLogoutReturnTo(),
        },
      }
      if (isMobileDevice) {
        logoutOptions.openUrl = async (url) => {
          await Browser.open({
            url,
            windowName: '_self',
          })
        }
      }
      void logout(logoutOptions)
      return
    }

    startSignupRedirect()
  }, [isMobileDevice, logout, startSignupRedirect])

  return (
    <div className="bg-light-blue flex justify-center items-center min-h-screen relative">
      <Loading />
    </div>
  )
}
export default Register
